import classNames from "classnames";
import { Entry } from "contentful";
import { useTranslations } from "next-intl";
import { CSSProperties, useMemo } from "react";
import { Control, Controller } from "react-hook-form";

import IconFilters from "@/icons/icon-filters";
import Icon from "~/components/common/icon";
import Checkbox from "~/components/form/checkbox";
import Editorials from "~/types/editorials";
import Constants from "~/utils/constants";

import editorialCardsStyles from "./editorial-cards.module.scss";
import styles from "./filter-cards-desktop.module.scss";

type Props = {
  activeCards: Entry<Editorials.Card>[];
  cardFilter: Editorials.EditorialCards["cardFilter"];
  disableFirstBoxOfCardFilter?: boolean;
  quantityFilters: Record<string, number>;
  scrollUp: boolean;
  activeFilters: string[];
  control: Control<any>;
  handleReset: () => void;
  handlePartialReset: (fields: string[]) => void;
};

export default function FilterCardsDesktop({
  activeCards,
  cardFilter,
  disableFirstBoxOfCardFilter,
  activeFilters,
  scrollUp,
  control,
  handleReset,
  handlePartialReset,
  quantityFilters,
}: Props) {
  const filterAllTag = Constants.FILTER_CARDS_STANDARD.all;
  const filterEditorialTag = Constants.FILTER_CARDS_STANDARD.editorial;
  const filterVideoTag = Constants.FILTER_CARDS_STANDARD.video;

  const t = useTranslations();

  const currentNumberCards = useMemo(() => {
    const currentCards = activeCards.filter((card, i) => {
      const contentfulTags = card.metadata.tags;
      let tags: string[] = [];

      if (contentfulTags.find((tag) => tag.sys.id === filterVideoTag)) {
        tags = contentfulTags.map((tag) => tag.sys.id).concat(filterAllTag);
      } else {
        tags = contentfulTags.map((tag) => tag.sys.id).concat([filterAllTag, filterEditorialTag]);
      }

      return activeFilters.length > 0 && activeFilters.every((filterId) => tags.includes(filterId));
    }).length;

    return currentCards || activeCards.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCards, activeFilters]);

  const styleGridRow = {
    "--editorial-cards-number-rows": Math.ceil(currentNumberCards / 3),
  } as CSSProperties;

  return (
    <div style={styleGridRow} className={classNames(styles.sidebar, scrollUp ? styles.scrollUp : undefined)}>
      <div className={styles.filterBoxHighlighted}>
        <div className={styles.filterBoxHighlightedTitle}>
          <Icon icon={IconFilters} width={16} height={16} />
          <p>{t("generic.filters")}</p>
          {activeFilters.length > 0 && (
            <span className={editorialCardsStyles.highlightValue}>{activeFilters.length}</span>
          )}
        </div>
        {activeFilters.length > 0 && (
          <button type="button" className={editorialCardsStyles.clearButton} onClick={() => handleReset()}>
            {t("generic.clear_all")}
          </button>
        )}
      </div>

      {!disableFirstBoxOfCardFilter && (
        <div className={editorialCardsStyles.filterBox}>
          <div className={editorialCardsStyles.wrapperClearButton}>
            <p className={editorialCardsStyles.filterBoxName}>{t("generic.tutorialType")}</p>
            <button
              className={classNames(
                editorialCardsStyles.clearButton,
                activeFilters.find((filter) => filter === filterVideoTag || filter === filterEditorialTag)
                  ? editorialCardsStyles.show
                  : editorialCardsStyles.hide
              )}
              onClick={() => handlePartialReset([filterVideoTag, filterEditorialTag])}
            >
              {t("generic.clear")}
            </button>
          </div>

          {quantityFilters[filterEditorialTag] && (
            <div className={editorialCardsStyles.filterBoxCheck}>
              <Controller
                name={filterEditorialTag}
                control={control}
                render={({ field }) => (
                  <Checkbox
                    {...field}
                    label={t("generic.editorial")}
                    checked={activeFilters.includes(filterEditorialTag)}
                  />
                )}
              />
              <span>{`(${quantityFilters[filterEditorialTag]})`}</span>
            </div>
          )}
          {quantityFilters[filterVideoTag] && (
            <div className={editorialCardsStyles.filterBoxCheck}>
              <Controller
                name={filterVideoTag}
                control={control}
                render={({ field }) => (
                  <Checkbox {...field} label={t("generic.video")} checked={activeFilters.includes(filterVideoTag)} />
                )}
              />
              <span>{`(${quantityFilters[filterVideoTag]})`}</span>
            </div>
          )}
        </div>
      )}

      {cardFilter &&
        cardFilter.length > 0 &&
        cardFilter.map((filter, i) => {
          const atLeastOneSelected = activeFilters.find((activeFilter) => filter.tags.includes(activeFilter));

          if (!filter.tags.find((tag) => quantityFilters[tag])) {
            return null;
          }

          return (
            <div key={filter.name + "-" + i} className={editorialCardsStyles.filterBox}>
              <div className={editorialCardsStyles.wrapperClearButton}>
                <p className={editorialCardsStyles.filterBoxName}>{filter.name}</p>
                <button
                  className={classNames(
                    editorialCardsStyles.clearButton,
                    atLeastOneSelected ? editorialCardsStyles.show : editorialCardsStyles.hide
                  )}
                  onClick={() => handlePartialReset(filter.tags)}
                >
                  {t("generic.clear")}
                </button>
              </div>
              {filter.tags.map((tag) => {
                if (!quantityFilters[tag]) {
                  return null;
                }

                return (
                  <div key={tag} className={editorialCardsStyles.filterBoxCheck}>
                    <Controller
                      name={tag}
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} label={t(`generic.${tag}`)} checked={activeFilters.includes(tag)} />
                      )}
                    />
                    <span>{`(${quantityFilters[tag]})`}</span>
                  </div>
                );
              })}
            </div>
          );
        })}
    </div>
  );
}
